"use client";

import useSWR from "swr";
import {
  SortProductSummariesRequest,
  SortedProductSummary,
} from "@/common/productSummary/types";

interface UseSortedProductSummariesOptions {
  enabled?: boolean;
}

const fetchSortedProductSummaries = async (
  key: [string, SortProductSummariesRequest],
): Promise<SortedProductSummary[]> => {
  const [url, sortRequest] = key;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sortRequest),
  });

  if (!response.ok) {
    throw new Error(`Failed to sort product summaries: ${response.status}`);
  }

  return response.json();
};

/**
 * Hook for sorting product summaries using the ML-powered sort endpoint
 */
const useSortedProductSummaries = (
  sortRequest: SortProductSummariesRequest | null,
  options: UseSortedProductSummariesOptions = {},
) => {
  const { enabled = true } = options;

  const shouldFetch =
    enabled && sortRequest && sortRequest.productSummaries.length > 0;
  const cacheKey = shouldFetch
    ? ["/api/product-summaries/sort", sortRequest]
    : null;

  const { data, error, isLoading } = useSWR(
    cacheKey,
    fetchSortedProductSummaries,
    {
      shouldRetryOnError: false,
    },
  );

  return { data, error, isLoading };
};

export default useSortedProductSummaries;
